
import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";

import {
  ClearRounded,
  VisibilityRounded,
} from "@mui/icons-material";

import {
  Box,
  Button,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";

import { Alert, Dialog, Table } from "../../layout";

import * as actions from "../../api/actions/pos";
import { controller, endpoints } from "../../api/actions/api";
import { AppContext } from "../../App";
import { IsEmptyObj } from "../../hooks/Method";
import { formatCurrency } from "../../hooks/Currency";

import ViewDetails from "./ViewDetails";
import ReportPrintButton from "../reports/ReportPrintButton";

//------------------------------------------------
// Inventory
//------------------------------------------------
const Inventory = (props) => {
  const { t } = useTranslation();

  const {
    _inventoryList,
    _getById,
    _delete,
    _defStore,
  } = props;

  const navigate = useNavigate();
  const { setLoader } = useContext(AppContext);

  const reportRef = useRef(null);

  const [viewData, setViewData] = useState({});
  const [showForm, setShowForm] = useState(false);

  const [alert, setAlert] = useState({
    isOpen: false,
    type: "",
    title: "",
    subTitle: "",
  });

  //------------------------------------------------
  // Add Inventory
  //------------------------------------------------
  const handleAddEdit = (val) => {
    if (val?.id) {
      navigate(`inventory-form/${val.id}`);
    } else {
      navigate("inventory-form");
    }
  };

  //------------------------------------------------
  // Delete Inventory
  //------------------------------------------------
  const handleDlt = (id) => {
    setAlert({
      isOpen: true,
      type: "confirmation",
      title: t("areYouSure"),
      subTitle: t("deleteRecordConfirmation"),

      onConfirm: () => {
        const onComplete = (res) => {
          if (res.status === 200) {
            setAlert({
              isOpen: true,
              type: "success",
              title: t("deletedSuccessfully"),
              subTitle: res.msg,
            });
          } else {
            setAlert({
              isOpen: true,
              type: "warning",
              title: t("warning"),
              subTitle: res.msg,
            });
          }
        };

        _delete(
          endpoints.Inventory,
          id,
          actions.ActionType.RmvInventory,
          onComplete
        );
      },
    });
  };

  //------------------------------------------------
  // Load Inventory by Store
  //------------------------------------------------
  useEffect(() => {
    if (_defStore?.id) {
      setLoader(true);

      _getById(
        endpoints.Inventory + controller.GetProdsByStore,
        _defStore.id,
        actions.ActionType.SetInventory,
        () => setLoader(false)
      );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_defStore?.id]);

  //------------------------------------------------
  // Inventory Table Data
  //------------------------------------------------
  const tableData = useMemo(() => {
    if (!Array.isArray(_inventoryList)) {
      return [];
    }

    return _inventoryList.map((obj) => {
      const product = obj.product || {};

      const quantity = Number(obj.inInventory) || 0;
      const sellPrice = Number(product.sellPrice) || 0;

      return {
        ...obj,

        name: product.name || "",
        barcode: product.barcode || "",

        addedUser: obj.user
          ? `${obj.user.firstName} ${obj.user.lastName}`
          : "",

        category: product.category?.name || "",
        subCat: product.parentCat?.name || "",

        invVal: formatCurrency(
          sellPrice * quantity,
          _defStore
        ),
      };
    });
  }, [_inventoryList, _defStore]);

  //------------------------------------------------
  // View Inventory Details
  //------------------------------------------------
  const handleView = (data) => {
    // Preserve original inventory object for ViewDetails.
    const original = _inventoryList.find(
      (item) => String(item.id) === String(data.id)
    );

    setViewData(original || data);
    setShowForm(true);
  };

  //------------------------------------------------
  // Table Columns
  //------------------------------------------------
  const screenColumns = [
    { id: "actions", label: t("actions") },
    { id: "name", label: t("name") },
    { id: "barcode", label: t("barcode") },
    { id: "category", label: t("category") },
    { id: "subCat", label: t("subcategory") },
    { id: "inInventory", label: t("inInventory") },
    { id: "invVal", label: t("inventoryValue") },
  ];

  const printColumns = screenColumns.filter(
    (column) => column.id !== "actions"
  );

  //------------------------------------------------
  // Action Buttons
  //------------------------------------------------
  const ActionComp = (data) => (
    <Box sx={{ display: "flex", gap: 0.5 }}>
      {/* View */}
      <Tooltip arrow title={t("view")}>
        <IconButton
          onClick={() => handleView(data)}
          size="small"
          className="btn-primary mr-5"
        >
          <VisibilityRounded className="fs-18 m-2" />
        </IconButton>
      </Tooltip>

      {/* Delete */}
      <Tooltip arrow title={t("delete")}>
        <IconButton
          onClick={() => handleDlt(data.id)}
          size="small"
          className="btn-error"
        >
          <ClearRounded className="fs-18 m-2" />
        </IconButton>
      </Tooltip>
    </Box>
  );

  //------------------------------------------------
  // Add Inventory Button
  //------------------------------------------------
  const HeaderComp = () => (
    <Button
      onClick={() => handleAddEdit()}
      className="btn-secondary"
      sx={{
        width: { md: "180px", xs: "100%" },
        mb: { xs: "10px", md: "0px" },
        fontSize: "17px",
        padding: "8px",
      }}
    >
      {t("addInventory")}
    </Button>
  );

  //------------------------------------------------
  // Render
  //------------------------------------------------
  return (
    <>
      {/* Header and Print Preview */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
          mb: 2,
        }}
      >
        <Typography variant="h5">
          {t("inventory")}
        </Typography>

        <ReportPrintButton
          reportRef={reportRef}
          paperSize="Letter"
          reportTitle={t("inventory")}
        />
      </Box>

      {/* Normal Inventory Table */}
      <Table
        ActionComp={ActionComp}
        HeaderComp={HeaderComp}
        tblHead={screenColumns}
        tblData={tableData}
      />

      {/* Printable Inventory List */}
      <Box
        ref={reportRef}
        className="inventory-list-print"
        sx={{
          position: "absolute",
          left: "-10000px",
          top: 0,
          width: "1000px",
          backgroundColor: "#fff",
          color: "#000",
          p: 2,
        }}
      >
        <Typography
          variant="h5"
          sx={{
            textAlign: "center",
            fontWeight: "bold",
            mb: 2,
          }}
        >
          {t("inventory")}
        </Typography>

        <Table
          printMode={true}
          ActionComp={() => <div />}
          HeaderComp={() => <div />}
          tblHead={printColumns}
          tblData={tableData}
        />
      </Box>

      {/* Alert */}
      <Alert
        alert={alert}
        setAlert={setAlert}
      />

      {/* Existing Inventory Details Dialog */}
      <Dialog
        show={showForm}
        maxWidth="lg"
        onClose={false}
      >
        {!IsEmptyObj(viewData) && (
          <ViewDetails
            setShowForm={setShowForm}
            viewData={viewData}
          />
        )}
      </Dialog>
    </>
  );
};

const mapStateToProps = (state) => ({
  _inventoryList: state.pos.inventoryList,
  _defStore: state.pos.defStore,
});

const mapActionToProps = {
  _getById: actions.getById,
  _delete: actions.Delete,
};

export default connect(
  mapStateToProps,
  mapActionToProps
)(Inventory);
